import { useState } from 'react';
import { usePersonalRating } from '../../api/hooks';
import { haptics } from '../../telegram';
import { track } from '../../analytics';
import { HexBadge } from './HexBadge';
import { DecayBar } from './DecayBar';
import { LevelLadderSheet } from './LevelLadderSheet';
import { fmtDayMonth } from './ratingFormat';

const HexIcon = ({ size = 10 }: { size?: number }) => (
  <svg width={size} height={(size * 26) / 24} viewBox="0 0 24 26" fill="none" stroke="currentColor" strokeWidth={2.6} strokeLinejoin="round">
    <path d="M12 1.3l10.2 5.9v11.6L12 24.7 1.8 18.8V7.2z" />
  </svg>
);

/**
 * Карточка «Личный рейтинг» (RA1) — в своём профиле между шапкой и витриной;
 * с `pid` — read-only гостевой вариант (R2-4): «#N в клубе» в шапке, пик
 * справа, без прогресса/декея/«Подробнее».
 */
export function RatingCard({ pid }: { pid: number | null }) {
  const { data } = usePersonalRating(pid);
  const [ladderOpen, setLadderOpen] = useState(false);
  if (!data) return <div className="skeleton" style={{ height: 118, borderRadius: 18, marginBottom: 14 }} />;

  const guest = pid !== null;
  const noGames = data.value === null;
  const atPeak = !noGames && data.peak !== null && data.peak.value === data.value;
  const next = data.levels.find((l) => l.n === data.level + 1) ?? null;
  const cur = data.levels.find((l) => l.n === data.level) ?? null;

  return (
    <div className="rating-card">
      <div className="rating-card-head">
        <span className="rc-head-title">
          <HexIcon /> Личный рейтинг
        </span>
        {guest ? (
          data.rank !== null && <span className="rc-head-rank mono">#{data.rank} в клубе</span>
        ) : (
          <button
            className="rc-head-more"
            onClick={() => {
              haptics.tap();
              track('rating_ladder_open', {});
              setLadderOpen(true);
            }}
          >
            Подробнее →
          </button>
        )}
      </div>

      {noGames ? (
        <div className="rating-card-row">
          <HexBadge level={0} size={34} dashed />
          <div className="rc-mid">
            <div className="rc-value mono">—</div>
            <div className="rc-sub">Личный рейтинг появится после первой игры. Все стартуют с 1000.</div>
          </div>
        </div>
      ) : (
        <>
          <div className="rating-card-row">
            <HexBadge level={data.level} size={34} />
            <div className="rc-mid">
              <div className={`rc-value mono ${data.decayActive ? 'rc-value--decay' : ''}`}>
                {data.decayActive && <span className="rc-clock" title="Неактивен">⧗</span>}
                {data.value}
                {atPeak && <span className="rc-chip-peak mono">ПИК</span>}
                {data.calibration && <span className="rc-chip-calib">предварительно</span>}
              </div>
              <div className="rc-sub">Уровень {data.level} · вся карьера</div>
            </div>
            <div className="rc-right">
              {guest ? (
                data.peak && (
                  <>
                    <div className="rc-peak-val mono">{data.peak.value}</div>
                    <div className="rc-peak-date">пик · {fmtDayMonth(data.peak.date)}</div>
                  </>
                )
              ) : data.lastDelta ? (
                <>
                  <div className={`rc-delta mono ${data.lastDelta.value >= 0 ? 'up' : 'down'}`}>
                    {data.lastDelta.value >= 0 ? `+${data.lastDelta.value}` : data.lastDelta.value}
                  </div>
                  <div className="rc-delta-date">за турнир {fmtDayMonth(data.lastDelta.date)}</div>
                </>
              ) : null}
            </div>
          </div>

          {atPeak && !guest && (
            <div className="rc-peak-note">Новый личный пик — он идёт в зачёт «Мастерства».</div>
          )}

          {!guest && data.calibration && (
            <div className="rc-calib">
              <div className="rc-calib-segs">
                {Array.from({ length: 5 }, (_, i) => (
                  <i key={i} className={i < data.gamesPlayed ? 'on' : ''} />
                ))}
              </div>
              <div className="rc-calib-note">
                Сыграй ещё {Math.max(0, 5 - data.gamesPlayed)}{' '}
                {plural(Math.max(0, 5 - data.gamesPlayed))}, чтобы попасть в рейтинг · {data.gamesPlayed}/5
              </div>
            </div>
          )}

          {!guest && !data.calibration && next && cur && data.value !== null && (
            <div className="rc-progress">
              <div className="rc-bar">
                <i
                  style={{
                    width: `${Math.min(100, Math.max(3, ((data.value - cur.threshold) / (next.threshold - cur.threshold)) * 100))}%`,
                  }}
                />
              </div>
              <div className="rc-bar-caps mono">
                <span>УР. {cur.n} · {cur.threshold}</span>
                <span className="mid">ДО УР. {next.n} — {Math.max(0, next.threshold - data.value)}</span>
                <span>{next.threshold}</span>
              </div>
            </div>
          )}

          {!guest && data.decay && data.decay.state !== 'none' && <DecayBar decay={data.decay} />}
        </>
      )}

      {ladderOpen && <LevelLadderSheet rating={data} onClose={() => setLadderOpen(false)} />}
    </div>
  );
}

function plural(n: number): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return 'игру';
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return 'игры';
  return 'игр';
}
